import { FC, useState } from 'react';

import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useApplication } from '@eneco/common/application';
import RichText from '@eneco/components/RichText/RichText';
import { ProductSwitchRecordDto } from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
import { useRouter } from '@eneco/next';
import { AlertDialog, Button, Checkbox, Grid, Stack, Stretch } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { ProductSwitchRenderingExtended } from '@sitecore/types/manual/be/ProductSwitchExtended';
import { addMonths, startOfMonth } from 'date-fns';
import { nanoid } from 'nanoid';
import { useFormContext } from 'react-hook-form';

import { CURRENT, SCHEMA_SELECTED_PRODUCTS_SWITCH_NAMES } from './ProductSwitch';
import { ProductSwitchHeading } from './ProductSwitchHeading';
import SummaryCard from './SummaryCard';
import { useDateFormatter } from '../../../hooks/useDateFormatter';

type ProductSwitchOverviewProps =
  | {
      sortedProducts: ProductSwitchRecordDto[];
      hasOrderId: false;
      editPreviousSelectedProductSwitch?: (ean: string) => void;
      isLoadingSubmit?: boolean;
    }
  | {
      sortedProducts: ProductSwitchRecordDto[];
      hasOrderId: true;
      editPreviousSelectedProductSwitch?: never;
      isLoadingSubmit?: boolean;
    };

const ProductSwitchOverview: FC<ProductSwitchOverviewProps> = ({
  sortedProducts,
  hasOrderId = false,
  editPreviousSelectedProductSwitch,
  isLoadingSubmit,
}) => {
  const { fields } = useContent<ProductSwitchRenderingExtended>();
  const { searchParams } = useApplication();
  const { longDate } = useDateFormatter();
  const { push } = useRouter();
  const { format } = useFormatter();
  const {
    watch,
    register,
    formState: { errors },
  } = useFormContext();

  const [isOpenCancelDialog, setIsOpenCancelDialog] = useState<boolean>(false);
  const { isCurrentAccountReader } = useSelfServiceAccount();

  const contractIdSearchParam = searchParams.get('contract');
  const selectedProductNames: string[] = hasOrderId
    ? sortedProducts.map(product => product.switchOptions?.[0]?.technicalProductName ?? '')
    : watch(SCHEMA_SELECTED_PRODUCTS_SWITCH_NAMES);

  return (
    <>
      <ProductSwitchHeading
        title={fields.summaryStep.title.value}
        subTitle={format(fields.summaryStep.description.value, {
          date: longDate(startOfMonth(addMonths(new Date(), 1))),
        })}
        sortedProducts={sortedProducts}
        stepperLabels={{
          electricityStepLabel: fields.navigation.electricityStepLabel.value,
          gasStepLabel: fields.navigation.gasStepLabel.value,
          summaryStepLabel: fields.navigation.summaryStepLabel.value,
        }}
        activeIndexProductSwitch={sortedProducts?.length}
        hasOrderId={hasOrderId}
      />
      <Stack direction="column" gap={6}>
        <Stack.Item>
          <Stretch>
            <Grid
              columns={{
                initial: 1,
                md: selectedProductNames.filter(name => name !== CURRENT).length <= 2 ? 2 : 4,
              }}
              alignY="stretch"
              gap={6}>
              {selectedProductNames.map((name, index) => (
                <ProductCard
                  productName={name}
                  index={index}
                  key={nanoid()}
                  sortedProducts={sortedProducts}
                  editPreviousSelectedProductSwitch={editPreviousSelectedProductSwitch}
                  hasOrderId={hasOrderId}
                />
              ))}
            </Grid>
          </Stretch>
        </Stack.Item>
        <Stack.Item>
          <Checkbox
            label={fields.summaryStep.termsAndConditionsCheckboxFormField.value.label}
            {...register('isAgreementChecked')}
            error={(errors?.isAgreementChecked?.message as string) ?? ''}
          />
        </Stack.Item>
      </Stack>
      {!isCurrentAccountReader && (
        <Stack direction="row" gap={4}>
          <Button type="submit" isLoading={isLoadingSubmit}>
            {fields.navigation.saveLink.value.text}
          </Button>
          <AlertDialog
            isOpen={isOpenCancelDialog}
            setOpen={setIsOpenCancelDialog}
            confirmText={fields.navigation.cancelDialog.value.submitButtonText ?? ''}
            denyText={fields.navigation.cancelDialog.value.cancelButtonText ?? ''}
            title={fields.navigation.cancelDialog.value.title}
            description={<RichText html={fields.navigation.cancelDialog.value.content} />}
            trigger={<Button action="secondary">{fields.navigation.cancelLink.value.text}</Button>}
            onConfirm={() => {
              push(`${fields.navigation.cancelLink.value.href}?contract=${contractIdSearchParam}`);
            }}
            onDeny={() => {}}
          />
        </Stack>
      )}
    </>
  );
};

const ProductCard = ({
  productName,
  index,
  sortedProducts,
  editPreviousSelectedProductSwitch,

  hasOrderId,
}: {
  productName: string;
  index: number;
  sortedProducts: ProductSwitchRecordDto[];
  editPreviousSelectedProductSwitch?: (ean: string) => void;
  hasOrderId: boolean;
}) => {
  const { fields } = useContent<ProductSwitchRenderingExtended>();
  const { locale } = useApplication();

  const isFrench = locale === 'fr-BE';
  if (!sortedProducts) return null;

  const originalProduct = sortedProducts?.[index]?.current;
  const newProduct = sortedProducts?.[index]?.switchOptions?.find(
    option => option.technicalProductName === productName,
  );

  if (newProduct == null || originalProduct == null) return null;

  const isElectricity = originalProduct?.energyType === 'Electricity';
  const fieldGroup = isElectricity ? fields.summaryElectricityCard : fields.summaryGasCard;

  return (
    <Grid.Item>
      <Stretch height={true}>
        <SummaryCard
          fieldGroup={fieldGroup}
          originalProduct={originalProduct}
          newProduct={newProduct}
          isFrench={isFrench}
          onEdit={editPreviousSelectedProductSwitch}
          hasOrderId={hasOrderId}
        />
      </Stretch>
    </Grid.Item>
  );
};

export default ProductSwitchOverview;
